/**
 * FolioStatusNotice — avisa en el formulario de emisión si el tipo de documento
 * elegido no tiene folios, o si le quedan pocos.
 *
 * Por qué existe: quedarse sin folios autorizados impide emitir, y hasta ahora
 * el usuario solo se enteraba al apretar "Emitir" y recibir el rechazo. En
 * producción un tenant estuvo con las notas de débito agotadas (el contador
 * quedó en 412 con el CAF hasta el 411) mientras el correo de aviso diario se
 * perdía entre el ruido.
 *
 * El umbral de "quedan pocos" NO se decide acá: lo resuelve el backend con el
 * valor configurado para ese tipo de documento (`config_tenant/folio_alerts.py`),
 * así que la pantalla y el correo siempre dicen lo mismo.
 */
import { AlertTriangle, XCircle } from 'lucide-react'
import { useCAFList, useDTETypes } from '../hooks/useConfig'

interface Props {
  /** Código interno del tipo de documento del formulario: 'FE', 'BNA', 'GD'… */
  documentType: string | undefined
  className?: string
}

export function FolioStatusNotice({ documentType, className = '' }: Props) {
  const { data: dteTypes } = useDTETypes()
  const { data: cafs } = useCAFList()

  if (!documentType || !dteTypes || !cafs) return null

  const siiCode = dteTypes.find(t => t.internal_code === documentType)?.sii_doc_type
  if (siiCode == null) return null

  const fila = cafs.find(c => c.document_type_code === siiCode)
  // Sin CAF cargado no se dice nada: el tenant puede estar emitiendo con un
  // proveedor que no usa folios propios, y ahí el aviso sería un falso positivo.
  if (!fila || !fila.has_caf) return null

  const disponibles = fila.folios_disponibles ?? 0
  const nombre = fila.document_type_label

  if (fila.estado === 'agotado' || disponibles <= 0) {
    return (
      <div
        role="alert"
        className={`flex items-start gap-2 rounded-lg border border-danger-border-strong bg-danger-surface px-3 py-2 text-xs text-danger-content-strong ${className}`}
      >
        <XCircle size={15} className="mt-px flex-shrink-0" />
        <span>
          <strong>Sin folios disponibles para {nombre}.</strong> No vas a poder emitir hasta
          cargar un CAF nuevo del SII en Configuración → Folios.
        </span>
      </div>
    )
  }

  if (fila.estado === 'por_agotarse') {
    return (
      <div
        role="status"
        className={`flex items-start gap-2 rounded-lg border border-warning-border-strong bg-warning-surface px-3 py-2 text-xs text-warning-content-strong ${className}`}
      >
        <AlertTriangle size={15} className="mt-px flex-shrink-0" />
        <span>
          {disponibles === 1 ? 'Queda' : 'Quedan'} <strong>{disponibles.toLocaleString('es-CL')}</strong>{' '}
          {disponibles === 1 ? 'folio' : 'folios'} de {nombre}. Conviene pedir un CAF nuevo al SII
          antes de que se agoten.
        </span>
      </div>
    )
  }

  return null
}
